// The description of an alarm in the user's language, for the errors a PLC reports.
//
// The backend resolves each PLC code (`001`…`009`) to a short English text (its `PlcErrorCatalog`) and
// stores it as `description`. The alarm's code says who reported it and ends with the PLC code:
// `CELL-7-001` for cell group 7, `DEV-12-001` for machine 12. The i18n key is `CELL-` plus that last part
// (`CELL-001`), so every reporter shares one translation, and the backend's text is the fallback when a
// language has none -- an unknown code keeps saying exactly what the backend sent. Codes stored before the
// reporter was added (`CELL-001`), or with the machine's name instead of its id (`DEV-Robot-001`), resolve
// to the same key.
//
// Only a last part of three digits is a PLC code. Other alarms end in something else -- the storage ones
// in a position (`CELL-STORAGE-3-POS-12`) -- and keep their own text.
//
// A cell code is translated by the code alone. A machine code is not: the drivers report `DEV-` codes with
// the same numbering and a different meaning, and only the Yaskawa handler describes them through the
// catalogue. So a `DEV-` code is translated only when its text is exactly the catalogue's text for that
// number -- the proof it came out of the catalogue. Any other machine's `001` keeps its own words:
// translating it as the cell's `001` would show the wrong fault.
const CELL_ERROR_CODE_PREFIX = 'CELL-'
const DEVICE_ERROR_CODE_PREFIX = 'DEV-'
const PLC_CODE = /^\d{3}$/

// The backend's catalogue, word for word: it is how a machine's alarm proves it came out of it. Part of the
// contract with sun (`contrato-sun.md` §5bis) -- if a text changes there, it changes here.
const PLC_CATALOG_TEXTS = Object.freeze({
  '001': 'air failure',
  '002': 'access door open',
  '003': 'emergency stop tripped',
  '004': 'cell reset. start pending',
  '005': 'cnc door position error',
  '006': 'cnc control not retracted',
  '007': 'part not detected',
  '008': 'unexpected gripper on robot',
  '009': 'part misplaced - storage'
})

const lastPartOf = (code) => code.slice(code.lastIndexOf('-') + 1)

// `CELL-7-001` → `CELL-001`; '' when the code does not end in a PLC code.
const catalogKeyOf = (code) => {
  const lastPart = lastPartOf(code)
  return PLC_CODE.test(lastPart) ? CELL_ERROR_CODE_PREFIX + lastPart : ''
}

const cellErrorDescription = (t_, alarm) => {
  const description = typeof alarm?.description === 'string' ? alarm.description : ''
  const code = typeof alarm?.code === 'string' ? alarm.code : ''
  const key = catalogKeyOf(code)
  if (key === '') return description
  if (code.startsWith(CELL_ERROR_CODE_PREFIX)) return t_(key, { defaultValue: description })
  if (code.startsWith(DEVICE_ERROR_CODE_PREFIX) && PLC_CATALOG_TEXTS[lastPartOf(code)] === description) {
    return t_(key, { defaultValue: description })
  }
  return description
}

export { CELL_ERROR_CODE_PREFIX, DEVICE_ERROR_CODE_PREFIX, PLC_CATALOG_TEXTS, catalogKeyOf, cellErrorDescription }
